/**
 * Branding panel of `/settings/tenant` (#2387) — `GET/PUT /api/tenant/branding`
 * and `PUT/DELETE /api/tenant/branding/logo`.
 *
 * Two rows (#2429): the organization name, shown as text until its Pencil
 * opens an input with "Save" / "Cancel"; and the logo shown at the top-left of
 * the sidebar, with a preview, a "Change" button that opens a hidden file
 * picker, and "Remove". A picked file is checked for type and size here only
 * to spare a doomed request; the server judges the type by magic bytes and is
 * the real gate. Each write refetches the branding, so the sidebar updates at
 * once.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@resource-kit/ui/react';
import { useTenantLogoSrc } from '@hooks/useTenantBranding';
import type { TenantBranding } from '@/services/tenants';
import {
  requestErrorMessage,
  useDeleteTenantLogo,
  useSaveTenantName,
  useUploadTenantLogo,
} from '../hooks/useTenants';
import { EditButton, SettingsPanel, SettingsRow } from './SettingsSection';

const LOGO_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_LOGO_BYTES = 256 * 1024;
const MAX_NAME_LENGTH = 64;
const DEFAULT_LOGO_SRC = '/logo.svg';
const NAME_INPUT_ID = 'tenant-branding-name';

export function BrandingCard({ branding }: { branding: TenantBranding }) {
  const { t } = useTranslation();
  const saveName = useSaveTenantName();
  const upload = useUploadTenantLogo();
  const remove = useDeleteTenantLogo();
  const logoSrc = useTenantLogoSrc(branding.logoUrl);
  const fileInput = useRef<HTMLInputElement>(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(branding.name);
  const [fileError, setFileError] = useState<string | null>(null);

  const trimmed = name.trim();

  const openName = () => {
    setName(branding.name);
    saveName.reset();
    setEditingName(true);
  };
  const cancelName = () => {
    setEditingName(false);
    setName(branding.name);
    saveName.reset();
  };
  const submitName = () =>
    saveName.mutate({ name: trimmed }, { onSuccess: () => setEditingName(false) });

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the picker so choosing the same file again still fires onChange.
    e.target.value = '';
    if (file === undefined) return;
    // An empty type means the browser could not tell; the server's magic-byte
    // check decides then.
    if (file.type !== '' && !LOGO_TYPES.includes(file.type)) {
      setFileError(t('tenants.branding.logoTypeInvalid'));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setFileError(t('tenants.branding.logoTooLarge'));
      return;
    }
    setFileError(null);
    upload.mutate(file);
  };

  return (
    <SettingsPanel testId="tenant-branding-card">
      <SettingsRow
        label={t('tenants.branding.name')}
        labelFor={editingName ? NAME_INPUT_ID : undefined}
        action={
          editingName ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelName}
                disabled={saveName.isPending}
              >
                {t('tenants.branding.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={submitName}
                disabled={trimmed.length === 0 || saveName.isPending}
              >
                {saveName.isPending ? t('tenants.branding.saving') : t('tenants.branding.save')}
              </Button>
            </>
          ) : (
            <EditButton label={t('tenants.branding.editName')} onClick={openName} />
          )
        }
      >
        {editingName ? (
          <Input
            id={NAME_INPUT_ID}
            className="h-9 max-w-xs"
            maxLength={MAX_NAME_LENGTH}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        ) : (
          <span className="font-medium">{branding.name}</span>
        )}
        {saveName.isError && (
          <p className="mt-2 text-sm text-destructive">
            {t('tenants.branding.saveFailed', { message: requestErrorMessage(saveName.error) })}
          </p>
        )}
        {!editingName && saveName.isSuccess && (
          <p className="mt-1 text-sm text-success">{t('tenants.branding.saved')}</p>
        )}
      </SettingsRow>

      <SettingsRow
        label={t('tenants.branding.logo')}
        action={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {t('tenants.branding.changeLogo')}
            </Button>
            {branding.logoUrl !== null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {t('tenants.branding.removeLogo')}
              </Button>
            )}
          </>
        }
      >
        <div className="flex items-center gap-3">
          <img
            data-testid="tenant-logo-preview"
            src={logoSrc ?? DEFAULT_LOGO_SRC}
            alt={t('tenants.branding.logoPreview')}
            className="h-12 w-12 shrink-0 rounded-md border bg-background object-contain p-1"
          />
          <div className="min-w-0 space-y-0.5">
            {branding.logoUrl === null && <p className="text-sm">{t('tenants.branding.noLogo')}</p>}
            <p className="text-xs text-muted-foreground">{t('tenants.branding.logoHint')}</p>
          </div>
        </div>
        <input
          ref={fileInput}
          id="tenant-branding-logo"
          data-testid="tenant-logo-input"
          type="file"
          accept={LOGO_TYPES.join(',')}
          disabled={upload.isPending}
          onChange={onFile}
          className="hidden"
        />
        {fileError !== null && <p className="mt-2 text-sm text-destructive">{fileError}</p>}
        {upload.isPending && (
          <p className="mt-2 text-sm text-muted-foreground">{t('tenants.branding.uploading')}</p>
        )}
        {upload.isError && (
          <p className="mt-2 text-sm text-destructive">
            {t('tenants.branding.uploadFailed', { message: requestErrorMessage(upload.error) })}
          </p>
        )}
        {remove.isError && (
          <p className="mt-2 text-sm text-destructive">
            {t('tenants.branding.removeFailed', { message: requestErrorMessage(remove.error) })}
          </p>
        )}
      </SettingsRow>
    </SettingsPanel>
  );
}
